import LibraryProgramModal from "@/features/LibraryProgramModal";
import TrashModal from "@/features/TrashModal";
import { CalendarRange } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ALL_SUBJECTS, DAYS } from "@/lib/constants";
import { Loader2, Plus, Trash2, Send, Library, X, Pencil, Copy } from "lucide-react";

const EMPTY_SECTION = { name: "", subject: "", page_start: "", page_end: "", bank_page_start: "", bank_page_end: "", questions: "" };
const CAT_CLS = { TYT: "bg-blue-100 text-blue-700 border-blue-200", AYT: "bg-amber-100 text-amber-700 border-amber-200" };
const RES_TYPE_CLS = { "Föy": "bg-sky-100 text-sky-700 border-sky-200", "Soru Bankası": "bg-amber-100 text-amber-700 border-amber-200", "Deneme": "bg-rose-100 text-rose-700 border-rose-200" };

const splitRange = (p) => {
  if (!p) return ["", ""];
  const [a, b] = String(p).split("-");
  return [a || "", b ?? ""];
};

export default function ResourceLibrary() {
  const [items, setItems] = useState([]);
  const [students, setStudents] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState("");
  const [publisher, setPublisher] = useState("");
  const [category, setCategory] = useState("TYT");
  const [resType, setResType] = useState("Föy");
  const [sections, setSections] = useState([{ ...EMPTY_SECTION }]);
  const [busy, setBusy] = useState(false);
  const [assignId, setAssignId] = useState(null);
  const [showProgram, setShowProgram] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [targetType, setTargetType] = useState("student");
  const [sid, setSid] = useState("");
  const [groupIds, setGroupIds] = useState([]);
  const [tracksSel, setTracksSel] = useState([]); // en fazla 3 alan/sınıf
  const toggleTrack = (t) =>
    setTracksSel((prev) => {
      if (prev.includes(t)) return prev.filter((x) => x !== t);
      if (prev.length >= 3) {
        toast.error("En fazla 3 sınıf/alan seçebilirsiniz");
        return prev;
      }
      return [...prev, t];
    });
  const [startWeek, setStartWeek] = useState(1);
  const [foyDay, setFoyDay] = useState("Pazartesi");
  const [sbDay, setSbDay] = useState("Çarşamba");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, st, m] = await Promise.all([api.get("/library-resources"), api.get("/students"), api.get("/meta")]);
      setItems(r.data);
      setStudents(st.data);
      setTracks(m.data.tracks || []);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setS = (i, k, v) => setSections((ss) => ss.map((s, j) => (j === i ? { ...s, [k]: v } : s)));

  const resetForm = () => {
    setEditId(null);
    setName("");
    setPublisher("");
    setCategory("TYT");
    setResType("Föy");
    setSections([{ ...EMPTY_SECTION }]);
  };

  const openNew = () => {
    resetForm();
    setModal(true);
  };

  const openEdit = async (id) => {
    try {
      const { data } = await api.get(`/library-resources/${id}`);
      setEditId(id);
      setName(data.name || "");
      setPublisher(data.publisher || "");
      setCategory(data.category || "TYT");
      setResType(data.res_type || "Föy");
      const secs = (data.sections || []).map((s) => {
        const [ps, pe] = splitRange(s.pages);
        const [bps, bpe] = splitRange(s.bank_pages);
        return { name: s.name || "", subject: s.subject || "", page_start: ps, page_end: pe, bank_page_start: bps, bank_page_end: bpe, questions: s.questions || "" };
      });
      setSections(secs.length ? secs : [{ ...EMPTY_SECTION }]);
      setModal(true);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const [copyData, setCopyData] = useState(null);
  const [copyBusy, setCopyBusy] = useState(false);

  const openCopy = async (id) => {
    try {
      const { data } = await api.get(`/library-resources/${id}`);
      const secs = (data.sections || []).map((s) => {
        const [ps, pe] = splitRange(s.pages);
        const [bps, bpe] = splitRange(s.bank_pages);
        return { name: s.name || "", subject: s.subject || "", page_start: ps, page_end: pe, bank_page_start: bps, bank_page_end: bpe, questions: s.questions || "", include: true };
      });
      setCopyData({ name: `${data.name || ""} (Kopya)`, publisher: data.publisher || "", category: data.category || "TYT", res_type: data.res_type || "Föy", sections: secs });
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const confirmCopy = async () => {
    if (!copyData) return;
    const chosen = copyData.sections.filter((s) => s.include);
    if (!chosen.length) return toast.error("En az bir bölüm seçin");
    if (!copyData.name.trim()) return toast.error("Kaynak adı zorunlu");
    setCopyBusy(true);
    try {
      await api.post("/library-resources", { name: copyData.name.trim(), publisher: copyData.publisher.trim(), category: copyData.category, res_type: copyData.res_type, sections: chosen });
      toast.success(`Kopya oluşturuldu (${chosen.length} bölüm)`);
      setCopyData(null);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setCopyBusy(false);
    }
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Kaynak adı zorunlu");
    const valid = sections.filter((s) => s.name.trim());
    if (!valid.length) return toast.error("En az bir bölüm girin (bölüm adı)");
    setBusy(true);
    try {
      const body = { name: name.trim(), publisher: publisher.trim(), category, res_type: resType, sections: valid };
      if (editId) {
        await api.put(`/library-resources/${editId}`, body);
        toast.success(`Kaynak güncellendi (${valid.length} bölüm)`);
      } else {
        await api.post("/library-resources", body);
        toast.success(`Kaynak kaydedildi (${valid.length} bölüm)`);
      }
      setModal(false);
      resetForm();
      load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id, rname) => {
    if (!window.confirm(`"${rname}" kaynağı ve öğrencilere atanmış tüm bölümleri/program görevleri silinecek. Emin misiniz?`)) return;
    try {
      await api.delete(`/library-resources/${id}`);
      toast.success(`"${rname}" çöp kutusuna taşındı (30 gün içinde geri getirilebilir)`);
      load();
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const toggleGroup = (id) => setGroupIds((g) => (g.includes(id) ? g.filter((x) => x !== id) : [...g, id]));

  const assign = async () => {
    if (targetType === "student" && !sid) return toast.error("Öğrenci seçin");
    if (targetType === "group" && groupIds.length === 0) return toast.error("Öğrenci seçin");
    if (targetType === "track" && tracksSel.length === 0) return toast.error("Alan / Sınıf seçin");
    setBusy(true);
    try {
      const body = { target: targetType, start_week: Number(startWeek), foy_day: foyDay, sb_day: sbDay };
      if (targetType === "student") body.student_id = sid;
      if (targetType === "group") body.student_ids = groupIds;
      if (targetType === "track") body.tracks = tracksSel;
      const { data } = await api.post(`/library-resources/${assignId}/assign`, body);
      toast.success(`${data.students} öğrenciye ${data.created} bölüm atandı — Çalışma Programı'nda görünür`);
      setAssignId(null);
      setSid("");
      setGroupIds([]);
      setTracksSel([]);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="resource-library">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold text-slate-400 flex items-center gap-1.5"><Library size={13} /> Kaynak Yönetimi</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900 mt-1">Kaynak Kütüphanesi</h1>
          <p className="text-slate-500 text-sm mt-1">Kaynak tanımla (yayınevi + bölüm + sayfa aralığı + soru), öğrenciye/alana/sınıfa ata, düzenle.</p>
        </div>
        <div className="flex gap-2">
          {showTrash && <TrashModal onClose={() => setShowTrash(false)} onRestored={load} />}
          <Button onClick={() => setShowTrash(true)} variant="outline" className="border-slate-300 text-slate-600" data-testid="lib-trash-btn">
            <Trash2 size={16} /> <span className="ml-1">Çöp Kutusu</span>
          </Button>
          <Button onClick={() => setShowProgram(true)} variant="outline" className="border-indigo-300 text-indigo-700" data-testid="lib-program-btn">
            <CalendarRange size={16} /> <span className="ml-1">Kaynak Program</span>
          </Button>
          <Button onClick={openNew} className="bg-slate-900 hover:bg-slate-800" data-testid="lib-new-btn">
            <Plus size={16} /> <span className="ml-1">Yeni Kaynak</span>
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-slate-500"><Loader2 className="animate-spin inline" size={22} /></div>
      ) : items.length === 0 ? (
        <div className="card-ta p-12 text-center text-slate-500">Henüz kaynak yok — "Yeni Kaynak" ile ilk kaynağı tanımlayın.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="lib-list">
          {items.map((r) => (
            <div key={r.id} className="card-ta p-4" data-testid={`lib-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  {r.publisher && <div className="text-[11px] font-bold text-indigo-600 uppercase tracking-wide truncate">{r.publisher}</div>}
                  <div className="font-display font-bold text-slate-900 truncate">{r.name}</div>
                  <div className="text-xs text-slate-500 mt-1">{r.sections} bölüm · {r.questions} soru</div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${RES_TYPE_CLS[r.res_type] || RES_TYPE_CLS["Föy"]}`}>{r.res_type || "Föy"}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${CAT_CLS[r.category] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{r.category}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Button size="sm" onClick={() => setAssignId(assignId === r.id ? null : r.id)} className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid={`lib-assign-${r.id}`}>
                  <Send size={12} /> <span className="ml-1">Öğrenciye Ata</span>
                </Button>
                <button onClick={() => openCopy(r.id)} className="ml-auto text-slate-400 hover:text-emerald-600" title="Kopyala" data-testid={`lib-copy-${r.id}`}><Copy size={15} /></button>
                <button onClick={() => openEdit(r.id)} className="text-slate-400 hover:text-indigo-600" title="Düzelt" data-testid={`lib-edit-${r.id}`}><Pencil size={15} /></button>
                <button onClick={() => del(r.id, r.name)} className="text-slate-300 hover:text-rose-600" title="Sil" data-testid={`lib-del-${r.id}`}><Trash2 size={15} /></button>
              </div>

              {assignId === r.id && (
                <div className="mt-3 rounded-xl border border-slate-200 p-3 space-y-3 bg-slate-50" data-testid={`lib-assign-box-${r.id}`}>
                  <div className="flex rounded-lg border border-slate-200 overflow-hidden bg-white">
                    {[["student", "Tek Öğrenci"], ["group", "Grup"], ["track", "Alan / Sınıf"]].map(([id, label]) => (
                      <button key={id} onClick={() => setTargetType(id)} data-testid={`lib-target-${id}`}
                        className={`flex-1 px-2 py-1.5 text-xs font-semibold transition-colors ${targetType === id ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}>{label}</button>
                    ))}
                  </div>
                  {targetType === "student" && (
                    <select className="ta-input" value={sid} onChange={(e) => setSid(e.target.value)} data-testid="lib-assign-student">
                      <option value="">Öğrenci seç</option>
                      {students.map((s) => <option key={s.id} value={s.id}>{s.name}{s.track ? ` · ${s.track}` : ""}</option>)}
                    </select>
                  )}
                  {targetType === "group" && (
                    <div className="max-h-36 overflow-y-auto space-y-1 bg-white rounded-lg border border-slate-200 p-2" data-testid="lib-assign-group">
                      {students.map((s) => (
                        <label key={s.id} className="flex items-center gap-2 text-xs px-1 py-1 rounded hover:bg-slate-50 cursor-pointer" data-testid={`lib-group-${s.id}`}>
                          <input type="checkbox" checked={groupIds.includes(s.id)} onChange={() => toggleGroup(s.id)} />
                          <span className="truncate">{s.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                  {targetType === "track" && (
                    <div className="space-y-2">
                      <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto bg-white rounded-lg border border-slate-200 p-2" data-testid={`lib-track-chips-${r.id}`}>
                        {tracks.map((t) => (
                          <button key={t} onClick={() => toggleTrack(t)} data-testid={`lib-track-chip-${t}`}
                            className={`text-xs px-2.5 py-1 rounded-full border font-semibold transition-colors ${tracksSel.includes(t) ? "bg-indigo-900 text-white border-indigo-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"}`}>{t}</button>
                        ))}
                      </div>
                      <div className="text-[11px] text-slate-400">Birden fazla seçebilirsiniz — en fazla 3 sınıf/alan ({tracksSel.length}/3)</div>
                    </div>
                  )}
                  <div className="flex items-end gap-2 flex-wrap">
                    <div>
                      <label className="ta-label">Başlangıç Haftası</label>
                      <input type="number" min="1" className="ta-input w-24" value={startWeek} onChange={(e) => setStartWeek(e.target.value)} data-testid="lib-assign-week" />
                    </div>
                    <div>
                      <label className="ta-label">1. Gün (Föy)</label>
                      <select className="ta-input w-auto" value={foyDay} onChange={(e) => setFoyDay(e.target.value)} data-testid="lib-assign-foy-day">
                        {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="ta-label">2. Gün (Soru Bankası)</label>
                      <select className="ta-input w-auto" value={sbDay} onChange={(e) => setSbDay(e.target.value)} data-testid="lib-assign-sb-day">
                        {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                    <Button onClick={assign} disabled={busy} className="bg-indigo-900 hover:bg-indigo-800 h-9" data-testid="lib-assign-submit">
                      {busy ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />} <span className="ml-1">Ata</span>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Kopya onay modalı */}
      {copyData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="lib-copy-modal">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setCopyData(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <button onClick={() => setCopyData(null)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700" data-testid="lib-copy-close"><X size={20} /></button>
            <h2 className="font-display text-xl font-extrabold text-slate-900">Kaynağı Kopyala</h2>
            <p className="text-sm text-slate-500 mt-0.5 mb-4">Kopyaya dahil edilecek bölümleri seçin.</p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="ta-label">Yeni Kaynak Adı</label>
                <input className="ta-input" value={copyData.name} onChange={(e) => setCopyData({ ...copyData, name: e.target.value })} data-testid="lib-copy-name" />
              </div>
              <div>
                <label className="ta-label">Yayınevi</label>
                <input className="ta-input" value={copyData.publisher} onChange={(e) => setCopyData({ ...copyData, publisher: e.target.value })} data-testid="lib-copy-publisher" />
              </div>
            </div>
            <div className="flex items-center justify-between mb-2">
              <label className="ta-label mb-0">Bölümler ({copyData.sections.filter((s) => s.include).length}/{copyData.sections.length})</label>
              <div className="flex gap-2 text-xs">
                <button onClick={() => setCopyData({ ...copyData, sections: copyData.sections.map((s) => ({ ...s, include: true })) })} className="text-indigo-600 font-semibold" data-testid="lib-copy-all">Tümü</button>
                <button onClick={() => setCopyData({ ...copyData, sections: copyData.sections.map((s) => ({ ...s, include: false })) })} className="text-slate-400 font-semibold" data-testid="lib-copy-none">Hiçbiri</button>
              </div>
            </div>
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {copyData.sections.map((s, i) => (
                <label key={i} className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`lib-copy-section-${i}`}>
                  <input type="checkbox" checked={s.include} onChange={() => setCopyData({ ...copyData, sections: copyData.sections.map((x, j) => (j === i ? { ...x, include: !x.include } : x)) })} />
                  <span className="font-medium text-slate-800 truncate flex-1">{s.name}</span>
                  <span className="text-xs text-slate-400 shrink-0">{s.subject} · {s.questions || 0} soru</span>
                </label>
              ))}
            </div>
            <Button onClick={confirmCopy} disabled={copyBusy} className="w-full mt-5 bg-emerald-600 hover:bg-emerald-700 h-11" data-testid="lib-copy-confirm">
              {copyBusy ? <Loader2 className="animate-spin" size={16} /> : <Copy size={16} />} <span className="ml-1">Kopyayı Oluştur</span>
            </Button>
          </div>
        </div>
      )}

      {/* Kaynak modalı (yeni / düzelt) */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="lib-modal">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
            <button onClick={() => setModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-700" data-testid="lib-modal-close"><X size={20} /></button>
            <h2 className="font-display text-xl font-extrabold text-slate-900">{editId ? "Kaynağı Düzelt" : "Yeni Kaynak"}</h2>
            <p className="text-sm text-slate-500 mt-0.5 mb-4">Bölümleri sayfa aralığı ve toplam soru sayısıyla tanımla.</p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div>
                <label className="ta-label">Yayınevi</label>
                <input className="ta-input" value={publisher} onChange={(e) => setPublisher(e.target.value)} placeholder="Örn: Hız Yayınları" data-testid="lib-publisher" />
              </div>
              <div>
                <label className="ta-label">Kaynak Adı</label>
                <input className="ta-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Örn: Matematik Soru Bankası" data-testid="lib-name" />
              </div>
              <div>
                <label className="ta-label">Kategori</label>
                <select className="ta-input" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="lib-category">
                  <option value="TYT">TYT</option>
                  <option value="AYT">AYT</option>
                  <option value="Genel">Genel</option>
                </select>
              </div>
              <div>
                <label className="ta-label">Kaynak Türü</label>
                <select className="ta-input" value={resType} onChange={(e) => setResType(e.target.value)} data-testid="lib-res-type">
                  <option value="Föy">Föy</option>
                  <option value="Soru Bankası">Soru Bankası</option>
                  <option value="Deneme">Deneme</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-between mb-2">
              <label className="ta-label mb-0">İçindekiler (Bölümler)</label>
              <Button variant="outline" size="sm" onClick={() => setSections((ss) => [...ss, { ...EMPTY_SECTION }])} className="border-slate-300 h-8 text-xs" data-testid="lib-section-add">
                <Plus size={13} /> <span className="ml-1">Bölüm Ekle</span>
              </Button>
            </div>

            <div className="hidden sm:grid grid-cols-[1fr_130px_58px_58px_58px_58px_58px_28px] gap-2 px-1 mb-1">
              {["Bölüm Adı", "Ders", "Föy Bş.", "Föy Bt.", "SB Bş.", "SB Bt.", "Soru", ""].map((h) => (
                <div key={h} className="text-[11px] font-semibold text-slate-500">{h}</div>
              ))}
            </div>
            <div className="space-y-2">
              {sections.map((s, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_130px_58px_58px_58px_58px_58px_28px] gap-2 items-center" data-testid={`lib-section-${i}`}>
                  <input className="ta-input" value={s.name} onChange={(e) => setS(i, "name", e.target.value)} placeholder={`${i + 1}. Ünite`} data-testid={`lib-section-name-${i}`} />
                  <select className="ta-input" value={s.subject} onChange={(e) => setS(i, "subject", e.target.value)} data-testid={`lib-section-subject-${i}`}>
                    <option value="">Ders</option>
                    {ALL_SUBJECTS.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                  <input type="number" className="ta-input" value={s.page_start} onChange={(e) => setS(i, "page_start", e.target.value)} title="Föy başlangıç" data-testid={`lib-section-ps-${i}`} />
                  <input type="number" className="ta-input" value={s.page_end} onChange={(e) => setS(i, "page_end", e.target.value)} title="Föy bitiş" data-testid={`lib-section-pe-${i}`} />
                  <input type="number" className="ta-input !border-amber-300" value={s.bank_page_start} onChange={(e) => setS(i, "bank_page_start", e.target.value)} title="Soru bankası başlangıç" data-testid={`lib-section-bps-${i}`} />
                  <input type="number" className="ta-input !border-amber-300" value={s.bank_page_end} onChange={(e) => setS(i, "bank_page_end", e.target.value)} title="Soru bankası bitiş" data-testid={`lib-section-bpe-${i}`} />
                  <input type="number" className="ta-input" value={s.questions} onChange={(e) => setS(i, "questions", e.target.value)} data-testid={`lib-section-q-${i}`} />
                  <button onClick={() => setSections((ss) => ss.filter((_, j) => j !== i))} className="text-rose-500 hover:text-rose-700" data-testid={`lib-section-del-${i}`}><X size={16} /></button>
                </div>
              ))}
            </div>

            <Button onClick={save} disabled={busy} className="w-full mt-5 bg-indigo-900 hover:bg-indigo-800 h-11" data-testid="lib-save">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} <span className="ml-1">{editId ? "Güncelle" : "Kaydet"}</span>
            </Button>
          </div>
        </div>
      )}

      {showProgram && <LibraryProgramModal resources={items} tracks={tracks} onClose={() => setShowProgram(false)} />}
    </div>
  );
}
